import { useSuspenseQuery } from '@apollo/client/react';
import { GQL_QUERY_READ_USAGE } from '../utility/queries';
import { useCallback, useTransition } from 'react';

export type UsageState = 'healthy' | 'notice' | 'warning';

const NOTICE_THRESHOLD = 50;
const WARNING_THRESHOLD = 20;

interface PoolView {
  total: number;
  used: number;
  remaining: number;
  percent: number;
}

const toPool = (balance: { total: number; used: number }): PoolView => {
  const total = balance.total;
  const used = balance.used;
  const remaining = Math.max(0, total - used);
  const percent = total > 0 ? Math.round((remaining / total) * 100) : 0;

  return { total, used, remaining, percent };
};

const stateFor = (percent: number): UsageState => {
  if (percent <= WARNING_THRESHOLD) return 'warning';
  if (percent <= NOTICE_THRESHOLD) return 'notice';
  return 'healthy';
};

export type UseUsageType = ReturnType<typeof useUsage>; 

export const useUsage = () => {
  const { data, refetch } = useSuspenseQuery(GQL_QUERY_READ_USAGE);

  const [fetching, startTransition] = useTransition(); 


  const query = toPool(data.readUsage.queries);

  const ingestion = toPool(data.readUsage.pages);

  // Warn off the most-depleted pool that actually has an allocation. Pools
  // with total === 0 are ignored — a customer with no ingestion balance
  // shouldn't see a warning permanently pinned at 0%.
  const activePercents = [query, ingestion]
    .filter((pool) => pool.total > 0)
    .map((pool) => pool.percent);

  const usageState: UsageState = stateFor(
    activePercents.length > 0 ? Math.min(...activePercents) : 100
  );

  const refresh = useCallback(() => void startTransition(() => void refetch()), []);

  return {
    usageState,
    refresh,
    fetching,
    usage: {
      query,
      ingestion,
    },
  };
};
